// ICAO radiotelephony pronunciation (Annex 10 Vol. II, Doc 9432): letters, digits, levels, headings,
// frequencies. Everything spoken on frequency is produced here so the TTS and the parser agree.

export const ALPHABET: Record<string, string> = {
  A: 'Alfa', B: 'Bravo', C: 'Charlie', D: 'Delta', E: 'Echo', F: 'Foxtrot', G: 'Golf', H: 'Hotel', I: 'India',
  J: 'Juliett', K: 'Kilo', L: 'Lima', M: 'Mike', N: 'November', O: 'Oscar', P: 'Papa', Q: 'Quebec', R: 'Romeo',
  S: 'Sierra', T: 'Tango', U: 'Uniform', V: 'Victor', W: 'Whiskey', X: 'X-ray', Y: 'Yankee', Z: 'Zulu',
};
export const DIGITS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'niner'];

/** Spells digits and letters one by one: '4TK' → 'four Tango Kilo'. */
export function spell(s: string): string {
  return s.toUpperCase().split('').filter(c => /[A-Z0-9]/.test(c)).map(c => (/\d/.test(c) ? DIGITS[Number(c)] : ALPHABET[c])).join(' ');
}

/** Altitude in feet the ICAO way: 4000 → 'four thousand', 2500 → 'two thousand five hundred', 12000 → 'one two thousand'. */
export function sayFeet(ft: number): string {
  const n = Math.round(ft / 100) * 100;
  const th = Math.floor(n / 1000), hu = Math.round((n % 1000) / 100);
  const parts: string[] = [];
  if (th) parts.push(th >= 10 ? spell(String(th)) : DIGITS[th]!, 'thousand');
  if (hu) parts.push(DIGITS[hu]!, 'hundred');
  return parts.join(' ') || 'zero';
}
export const sayFL = (fl: number): string => `flight level ${spell(String(Math.round(fl)).padStart(2, '0'))}`;
export const sayHeading = (hdg: number): string => spell(String(Math.round(hdg) % 360 === 0 ? 360 : Math.round(hdg) % 360).padStart(3, '0'));
export const saySpeed = (kt: number): string => spell(String(Math.round(kt)));
export const sayRunway = (rwy: string): string => {
  const m = /^(\d{1,2})([LCR]?)$/.exec(rwy);
  if (!m) return rwy;
  const side = { L: ' left', C: ' center', R: ' right', '': '' }[m[2] as 'L' | 'C' | 'R' | ''];
  return spell(m[1]!.padStart(2, '0')) + side;
};
/** 8.33 kHz channel: all six digits unless the last two are zero ('121.755' → 'one two one decimal seven five five'). */
export function sayFreq(freq: string): string {
  const [a, b = ''] = freq.split('.');
  let dec = b.padEnd(3, '0');
  if (dec.endsWith('00')) dec = dec.slice(0, 1);
  return `${spell(a!)} decimal ${spell(dec)}`;
}
export const saySquawk = (code: string): string => spell(code);
export const sayQnh = (hpa: number): string => `QNH ${spell(String(Math.round(hpa)))}`;
export function sayWind(dir: number, kt: number, gust?: number, variable = false): string {
  if (kt < 1) return 'wind calm';
  const d = variable ? 'variable' : `${spell(String(Math.round(dir / 10) * 10 || 360).padStart(3, '0'))} degrees`;
  return `wind ${d} ${spell(String(Math.round(kt)))} knots${gust ? ` gusting ${spell(String(Math.round(gust)))} knots` : ''}`;
}
/** Altitude below the transition level in feet, above as flight level. */
export function sayLevel(ft: number, isFL: boolean): string {
  return isFL ? sayFL(ft / 100) : `${sayFeet(ft)} feet`;
}
export function showLevel(ft: number, isFL: boolean): string {
  return isFL ? `FL${String(Math.round(ft / 100)).padStart(3, '0')}` : `${Math.round(ft)} ft`;
}
/** Converts a numeric word sequence in a text to digits – used by the parser. */
export const WORD_DIGIT: Record<string, string> = {
  zero: '0', one: '1', two: '2', three: '3', tree: '3', four: '4', fower: '4', five: '5', fife: '5', six: '6',
  seven: '7', eight: '8', nine: '9', niner: '9', to: '2', too: '2', for: '4', won: '1', ate: '8',
};
export const WORD_LETTER: Record<string, string> = Object.fromEntries(
  Object.entries(ALPHABET).flatMap(([k, v]) => [[v.toLowerCase(), k], [v.toLowerCase().replace('-', ''), k]]),
);
WORD_LETTER.alpha = 'A';
WORD_LETTER.juliet = 'J';
WORD_LETTER.xray = 'X';
WORD_LETTER.whisky = 'W';
